import {Editor, Extensions} from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import HardBreak from "@tiptap/extension-hard-break";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {Markdown} from "tiptap-markdown";
import {imageLayoutStyle, parseImageLayout} from "../TextAreaInputMarkdown/markdownImage";

/**
 * Editor-Konfiguration für Markdown-Felder („Infos für die Website“). Gespeichert wird weiter Markdown,
 * damit Buchungs-App und öffentliche Kursseiten unverändert bleiben. Probelauf 14.10.2026: alle 2.528
 * Prod-Texte gingen ohne sichtbare Änderung durch Einlesen und Speichern.
 */

// Einfacher Umbruch als schlichtes Zeilenende: Die Buchungs-App zeigt Zeilenenden per `white-space: pre-wrap`.
// Der Standard (`\` + Zeilenende) ergäbe dort <br> plus sichtbaren Umbruch, also doppelte Abstände.
const PlainHardBreak = HardBreak.extend({
    addStorage() {
        return {
            markdown: {
                serialize(state: {write: (text: string) => void}) {
                    state.write("\n");
                },
                parse: {},
            },
        };
    },
});

// Größe und Ausrichtung stehen als Stichworte im Bildtitel („klein rechts“), wie in der Buchungs-App ausgewertet.
type MarkdownState = {write: (text: string) => void; esc: (text: string) => string; closeBlock: (node: unknown) => void};
type ImageNode = {attrs: {src: string; alt?: string | null; title?: string | null}};

const LayoutImage = Image.extend({
    addAttributes() {
        return {
            ...(this.parent ? this.parent() : {}),
            // renderHTML schreibt das Layout nach data-layout (kein Tooltip); beim Verschieben, Kopieren und
            // Einfügen im Editor wird das gerenderte <img> wieder eingelesen, also hier zurücklesen.
            title: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-layout") || element.getAttribute("title") || null,
            },
        };
    },
    addStorage() {
        return {
            markdown: {
                // Bild als eigener Absatz; der Standard schließt den Block nicht, dann klebt der folgende Text am Bild.
                serialize(state: MarkdownState, node: ImageNode) {
                    const {src, alt, title} = node.attrs;
                    const titlePart = title ? ` "${title.replace(/"/g, '\\"')}"` : "";

                    state.write(`![${state.esc(alt || "")}](${src.replace(/[()]/g, "\\$&")}${titlePart})`);
                    state.closeBlock(node);
                },
                parse: {},
            },
        };
    },
    renderHTML({HTMLAttributes}) {
        const style = imageLayoutStyle(parseImageLayout(HTMLAttributes.title));
        const css = Object.entries(style)
            .map(([key, value]) => `${key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}: ${value}`)
            .join("; ");

        return ["img", {src: HTMLAttributes.src, alt: HTMLAttributes.alt, "data-layout": HTMLAttributes.title || "", style: css}];
    },
});

export const createRichTextExtensions = (placeholder = ""): Extensions => [
    // Alles, was vorhandene Texte nutzen (auch Überschriften H1–H6, Zitate, Code), muss lesbar bleiben,
    // sonst ändert sich ihre Darstellung beim ersten Speichern. Die Werkzeugleiste bietet nur einen Teil davon an.
    StarterKit.configure({hardBreak: false}),
    PlainHardBreak,
    LayoutImage.configure({allowBase64: false}),
    // autolink aus: sonst würden vorhandene nackte Adressen beim ersten Speichern zu Links.
    Link.configure({autolink: false, openOnClick: false, linkOnPaste: true}),
    Placeholder.configure({placeholder}),
    Markdown.configure({html: false, breaks: true, linkify: false, transformPastedText: true, transformCopiedText: true}),
];

export const getMarkdown = (storage: unknown): string => (storage as {markdown: {getMarkdown: () => string}}).markdown.getMarkdown();

/**
 * Teilt die Absätze im markierten Bereich an ihren Zeilenumbrüchen in eigene Absätze. Vorhandene Texte bestehen
 * oft aus einem Absatz mit einfachen Umbrüchen; ohne Teilen würde eine Liste oder Überschrift den ganzen Absatz
 * erfassen statt nur die markierten Zeilen. In der Buchungs-App sehen Absatz und Umbruch gleich aus (margin 0).
 */
export const splitLinesInSelection = (editor: Editor): boolean =>
    editor.commands.command(({tr, state}) => {
        const breaks: number[] = [];

        state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
            if (node.type.name === "paragraph" || node.type.name === "heading") {
                node.forEach((child, offset) => {
                    if (child.type.name === "hardBreak") {
                        breaks.push(pos + 1 + offset);
                    }
                });

                return false;
            }

            return true;
        });

        // Von hinten nach vorn, damit die früheren Positionen gültig bleiben.
        breaks
            .sort((a, b) => b - a)
            .forEach((pos) => {
                tr.delete(pos, pos + 1);
                tr.split(pos);
            });

        return true;
    });

/**
 * Wie `processMarkdown` in der Buchungs-App (CourseDetails): `Text\n---` ist dort eine Trennlinie, kein
 * Setext-H2. Ohne diese Normalisierung würde der Editor eine Überschrift daraus machen und als `## Text` speichern.
 */
export const normalizeLegacyMarkdown = (markdown: string): string => markdown.replace(/(.*)\n---/g, "$1\n\n---\n\n");

/**
 * Merkt eine Position im Dokument und führt sie bei jeder Änderung mit (Tippen, Klicken, Einfügen), z. B. während
 * ein Bild hochlädt. `stop()` beendet das Mitführen.
 */
export const trackPosition = (editor: Editor, start: number): {get: () => number; stop: () => void} => {
    let pos = start;
    const onTransaction = ({transaction}: {transaction: {mapping: {map: (position: number) => number}}}) => {
        pos = transaction.mapping.map(pos);
    };

    editor.on("transaction", onTransaction);

    return {get: () => pos, stop: () => editor.off("transaction", onTransaction)};
};

/**
 * Fügt ein Bild an `pos` ein, ohne eine Markierung zu ersetzen. Am Anfang eines Absatzes kommt es davor: kein leerer
 * Absatz über dem Bild, und in einem leeren Feld bleibt die Zeile zum Weiterschreiben. Mitten im Text teilt das Bild
 * (ein Block) den Absatz.
 */
export const insertImageAt = (editor: Editor, pos: number, attrs: {src: string; alt: string; title: string}): void => {
    const position = Math.min(pos, editor.state.doc.content.size);
    const $pos = editor.state.doc.resolve(position);
    const image = {type: "image", attrs};

    if ($pos.depth > 0 && $pos.parentOffset === 0) {
        editor.chain().insertContentAt($pos.before(), image).run();
    } else {
        editor.chain().insertContentAt(position, image).run();
    }
};
